import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  List,
  message,
  Modal,
  Select,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../UserContext';
import {
  ApiAppointment,
  AppointmentType,
  BaseComponentProps,
} from '../../types';
import {
  formatDate,
  getAgentTimezone,
  getCrmLink,
  renderContent,
} from './utils';
import { fetchAppointmentTypes } from '../../utils';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface AppointmentFormData {
  lead_source_id?: string;
  due_date: dayjs.Dayjs;
  expired_at: dayjs.Dayjs;
  title: string;
  description?: string;
  appointment_type: string;
  sms?: string;
  meeting_length?: dayjs.Dayjs;
}

export const Appointments = ({
  agent,
  apiUrl,
  clientConfig,
}: BaseComponentProps): JSX.Element => {
  const user = useContext(UserContext);
  const [appointments, setAppointments] = useState<
    ApiAppointment[] | undefined
  >(undefined);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [defaultDuration, setDefaultDuration] = useState<number>(30);
  const agentTimezone = getAgentTimezone(agent, clientConfig);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm();

  const fetchAgentInfo = async () => {
    try {
      if (!user?.accessToken) {
        return;
      }
      const response = await fetch(`${apiUrl}/api/agent/info/${agent.id}`, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
      });
      if (response.ok) {
        const agentData = await response.json();
        if (agentData.personalization?.default_appointment_duration_minutes) {
          setDefaultDuration(
            agentData.personalization.default_appointment_duration_minutes,
          );
        }
      }
    } catch (err) {
      console.error('Failed to fetch agent info:', err);
    }
  };

  const fetchAppointments = async (agent: any) => {
    try {
      setLoading(true);
      if (!user?.accessToken) {
        console.error('No access token available');
        return;
      }

      const url = `${apiUrl}/api/agents/${agent.id}/appointments`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch appointments: ${response.status}`);
      }

      const jsonData = await response.json();
      setAppointments(jsonData);
    } catch (err) {
      console.error('An error occurred while fetching appointments:', err);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointmentTypesData = async () => {
    try {
      if (clientConfig?.client_id && user?.accessToken) {
        const types = await fetchAppointmentTypes(
          apiUrl,
          clientConfig.client_id.toString(),
          user.accessToken,
        );
        setAppointmentTypes(types);
      }
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
      message.error('Failed to load appointment types');
    }
  };

  useEffect(() => {
    fetchAppointments(agent).then().catch(console.log);
    fetchAgentInfo();
    fetchAppointmentTypesData();
  }, [agent, clientConfig?.client_id]);

  const sortedAppointments = appointments?.slice().sort((a, b) => {
    const dateA = new Date(a.start_time);
    const dateB = new Date(b.start_time);
    return dateA.getTime() - dateB.getTime();
  });

  const getAppointmentTypeName = (appointmentType: any): string => {
    if (!appointmentType) {
      return 'N/A';
    }

    if (typeof appointmentType === 'string') {
      return appointmentType;
    }

    if (typeof appointmentType === 'object' && appointmentType.name) {
      return appointmentType.name;
    }

    return JSON.stringify(appointmentType);
  };

  const handleCreateAppointment = () => {
    form.resetFields();
    const hours = Math.floor(defaultDuration / 60);
    const minutes = defaultDuration % 60;
    const defaultTime = dayjs().hour(hours).minute(minutes);
    form.setFieldValue('meeting_length', defaultTime);
    setModalVisible(true);
  };

  const handleSubmit = async (values: AppointmentFormData) => {
    try {
      if (!user?.accessToken) {
        message.error('No access token available');
        return;
      }
      console.log('Form values:', values);
      const required = [
        values.due_date && dayjs.isDayjs(values.due_date),
        values.meeting_length && dayjs.isDayjs(values.meeting_length),
        values.title && values.title.trim(),
        values.appointment_type && values.appointment_type.trim(),
      ];
      if (required.some(v => !v)) {
        message.error('All required fields must be filled and valid.');
        return;
      }
      // Derive end time from start time + meeting length (expired_at may not be set if user didn't change meeting length)
      const dueDate = values.due_date;
      const meetingLength = values.meeting_length!;
      const expiredAt =
        values.expired_at && dayjs.isDayjs(values.expired_at)
          ? values.expired_at
          : dueDate
              .add(meetingLength.hour(), 'hour')
              .add(meetingLength.minute(), 'minute');
      // Interpret the chosen times as agent's local time, then send ISO UTC so the server gets timezone-aware datetimes
      const dueDateStr = dueDate.format('YYYY-MM-DD HH:mm:ss');
      const expiredAtStr = expiredAt.format('YYYY-MM-DD HH:mm:ss');
      const dueDateInAgentTz = dayjs.tz(dueDateStr, agentTimezone);
      const expiredAtInAgentTz = dayjs.tz(expiredAtStr, agentTimezone);
      const payload: any = {
        agent_id: agent.id,
        client_id: clientConfig?.client_id,
        due_date: dueDateInAgentTz.toISOString(),
        expired_at: expiredAtInAgentTz.toISOString(),
        title: values.title.trim(),
        appointment_type: values.appointment_type.trim(),
      };
      if (values.lead_source_id?.trim()) {
        payload.lead_source_id = values.lead_source_id.trim();
      }
      if (values.description?.trim()) {
        payload.description = values.description.trim();
      }
      if (values.sms) {
        payload.sms = values.sms;
      }
      const response = await fetch(`${apiUrl}/api/appointments/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify(payload),
      });
      const text = await response.text();
      if (!response.ok) {
        throw new Error(text);
      }
      message.success('Appointment created successfully');
      setModalVisible(false);
      form.resetFields();
      await fetchAppointments(agent);
    } catch (error) {
      console.error('Error creating appointment:', error);
      message.error(
        error instanceof Error ? error.message : 'Failed to create appointment',
      );
    }
  };

  return (
    <div>
      <Spin spinning={loading}>
        <Flex style={{ height: '70vh', overflowY: 'auto' }} vertical>
          <Flex
            align="center"
            justify="space-between"
            style={{ marginBottom: '16px' }}
          >
            <Button onClick={() => void fetchAppointments(agent)}>
              Refresh
            </Button>
            <Button onClick={handleCreateAppointment} type="primary">
              Create Appointment
            </Button>
          </Flex>

          {appointments && appointments.length === 0 && (
            <Text style={{ margin: '20px 0', textAlign: 'center' }}>
              No appointments found for this agent.
            </Text>
          )}

          <List
            dataSource={sortedAppointments}
            itemLayout="vertical"
            renderItem={appointment => {
              const isPast = new Date(appointment.start_time) < new Date();
              const crmLink = getCrmLink(
                clientConfig,
                appointment.lead_source_id,
              );
              return (
                <List.Item
                  extra={
                    <div style={{ textAlign: 'right' }}>
                      {isPast ? (
                        <Tag color="orange">Past</Tag>
                      ) : (
                        <Tag color="blue">Upcoming</Tag>
                      )}
                      {appointment.status && (
                        <Tag
                          color={
                            appointment.status === 'Completed'
                              ? 'green'
                              : 'default'
                          }
                        >
                          {appointment.status}
                        </Tag>
                      )}
                    </div>
                  }
                  style={{
                    background: '#f5f5f5',
                    padding: '16px',
                    borderRadius: '8px',
                    margin: '8px 0',
                  }}
                >
                  <List.Item.Meta
                    description={
                      <div>
                        <Text strong>{appointment.title || 'Appointment'}</Text>
                        {appointment.lead && (
                          <div>
                            <Text>
                              With:{' '}
                              {appointment.lead_source_id && crmLink ? (
                                <a
                                  href={crmLink}
                                  rel="noopener noreferrer"
                                  target="_blank"
                                >
                                  {appointment.lead.first_name}{' '}
                                  {appointment.lead.last_name}
                                </a>
                              ) : (
                                `${appointment.lead.first_name} ${appointment.lead.last_name}`
                              )}
                            </Text>
                          </div>
                        )}
                      </div>
                    }
                    title={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text>
                          {formatDate(appointment.start_time, agentTimezone)}
                          {appointment.end_time &&
                            ` to ${formatDate(appointment.end_time, agentTimezone)}`}
                        </Text>
                      </div>
                    }
                  />
                  {appointment.description && (
                    <div style={{ marginTop: '10px' }}>
                      <Text type="secondary">Details:</Text>
                      <div
                        style={{
                          background: '#fff',
                          padding: '8px',
                          borderRadius: '4px',
                          marginTop: '4px',
                        }}
                      >
                        {renderContent(appointment.description)}
                      </div>
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: '10px',
                      fontSize: '12px',
                      color: '#888',
                    }}
                  >
                    <div>
                      Appointment ID: {appointment.id}
                      {appointment.lead_source_id &&
                        ` | Lead ID: ${appointment.lead_source_id}`}
                    </div>
                    {appointment.appointment_type && (
                      <div>
                        Type:{' '}
                        {getAppointmentTypeName(appointment.appointment_type)}
                      </div>
                    )}
                    {appointment.outcome && (
                      <div>
                        Outcome:{' '}
                        {typeof appointment.outcome === 'object'
                          ? JSON.stringify(appointment.outcome)
                          : appointment.outcome}
                      </div>
                    )}
                  </div>
                </List.Item>
              );
            }}
          />
        </Flex>
      </Spin>
      <Modal
        footer={null}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        open={modalVisible}
        title="Create Appointment"
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="Lead Source ID" name="lead_source_id">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item
            label="Start Time"
            name="due_date"
            rules={[{ required: true, message: 'Please select due date' }]}
          >
            <DatePicker
              format="YYYY-MM-DD HH:mm:ss"
              showTime
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            label="Meeting Length"
            name="meeting_length"
            rules={[{ required: true, message: 'Please enter meeting length' }]}
          >
            <DatePicker
              format="HH:mm"
              onChange={time => {
                const dueDate = form.getFieldValue('due_date');
                if (
                  dueDate &&
                  dayjs.isDayjs(dueDate) &&
                  time &&
                  dayjs.isDayjs(time)
                ) {
                  const hours = time.hour();
                  const minutes = time.minute();
                  const expiredAt = dueDate
                    .add(hours, 'hour')
                    .add(minutes, 'minute');
                  form.setFieldValue('expired_at', expiredAt);
                }
              }}
              picker="time"
              placeholder="Select meeting duration"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item hidden name="expired_at">
            <Input />
          </Form.Item>
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please enter title' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item
            label="Appointment Type"
            name="appointment_type"
            rules={[
              { required: true, message: 'Please select appointment type' },
            ]}
          >
            <Select>
              {appointmentTypes.map(type => (
                <Option key={type.id} value={type.name}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit" type="primary">
              Create
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
